import { useState, useEffect, useRef } from "react";
import { NavLink, Link, useLocation } from "react-router-dom";
import {
  LayoutDashboard, Users2, Settings, Plus,
  LogOut, Menu, X, ChevronDown, ListTodo, SlidersHorizontal, BarChart3,
  Download, UsersRound, Trash2, Sun, Moon, PanelLeftClose, PanelLeftOpen,
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { useTheme } from "@/contexts/ThemeContext";
import { logPageView } from "@/lib/audit";
import { LogoWordmark, LogoIcon } from "@/components/Logo";
import NotificationBell from "@/components/NotificationBell";
import GlobalSearch from "@/components/GlobalSearch";
import { initials } from "@/lib/format";

// Grouped, accordion-aware navigation. Items with `children` render as an
// expandable group; leaf items navigate directly. `perm` gates visibility.
const NAV = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard },
  {
    label: "Leads", icon: Users2, children: [
      { to: "/leads/all", label: "All Leads" },
      { to: "/leads/pipeline", label: "Pipeline" },
      { to: "/leads/review-queue", label: "Review Queue", perm: "reports.view_team", badge: true },
    ],
  },
  {
    label: "Activities", icon: ListTodo, children: [
      { to: "/activities", label: "All Activities", badge: true, end: true },
      { to: "/activities/meetings", label: "Meetings" },
    ],
  },
  {
    label: "Automation", icon: SlidersHorizontal, children: [
      { to: "/automation/lead-qualification", label: "Lead Qualification", perm: "flow_builder.view" },
      { to: "/automation/stage-rules", label: "Stage Rules", perm: "flow_builder.view" },
      { to: "/automation/assignment-rules", label: "Assignment Rules", perm: "assignment_rules.view" },
      { to: "/automation/deal-forms", label: "Deal Forms", perm: "settings.edit" },
      { to: "/automation/pipeline-settings", label: "Pipeline Settings", perm: "pipeline_settings.view" },
      { to: "/automation/operations-sla", label: "Operations & SLA", perm: "settings.edit" },
      { to: "/automation/meta-ads", label: "Meta Ads", perm: "settings.edit" },
      { to: "/automation/scoring", label: "Scoring", perm: "scoring.view" },
    ],
  },
  {
    label: "Reports", icon: BarChart3, children: [
      { to: "/reports/leakage", label: "Sales Leakage", perm: "reports.view_team" },
      { to: "/reports/salesperson", label: "Salesperson Performance", perm: "reports.view_team" },
      { to: "/reports/source-quality", label: "Source Quality", perm: "reports.view_team" },
      { to: "/reports/workload-audit", label: "Workload Audit", perm: "reports.view_team" },
      { to: "/reports/manager-control", label: "Manager Control", perm: "reports.view_team" },
      { to: "/reports/meta-performance", label: "Source / Meta Performance", perm: "reports.view_team" },
      { to: "/reports/fairness", label: "Fairness Report", perm: "reports.view_team" },
    ],
  },
  { to: "/import-center", label: "Import Center", icon: Download, perm: "leads.import_csv" },
  {
    label: "Team", icon: UsersRound, children: [
      { to: "/team", label: "Members", perm: "team.view", end: true },
      { to: "/team/timesheets", label: "Timesheets", perm: "timesheets.view" },
    ],
  },
  { to: "/recycle-bin", label: "Recycle Bin", icon: Trash2, perm: "recycle_bin.view" },
  { to: "/settings", label: "Settings", icon: Settings },
];

const slug = (s) => s.toLowerCase().replace(/\s+/g, "-");

function LeafNavItem({ item, badgeCount, onClick }) {
  return (
    <NavLink
      to={item.to}
      onClick={onClick}
      data-testid={`nav-${slug(item.label)}`}
      className={({ isActive }) =>
        `group flex items-center gap-3 rounded-xl px-3.5 py-2.5 text-sm font-medium transition-all ${
          isActive ? "bg-loop text-white shadow-sm loop-glow" : "text-ink/70 hover:bg-sky/50 hover:text-navy"
        }`
      }
    >
      {({ isActive }) => (
        <>
          <item.icon className="h-[18px] w-[18px]" strokeWidth={isActive ? 2.2 : 1.8} />
          <span className="flex-1 font-sora">{item.label}</span>
          {item.badge && badgeCount > 0 && (
            <span className={`min-w-[20px] rounded-full px-1.5 py-0.5 text-center text-[11px] font-bold ${isActive ? "bg-white text-loop" : "bg-red-500 text-white"}`}>{badgeCount}</span>
          )}
        </>
      )}
    </NavLink>
  );
}

function ChildNavItem({ item, badgeCount, onClick }) {
  return (
    <NavLink
      to={item.to}
      onClick={onClick}
      end={item.end}
      data-testid={`nav-${slug(item.label)}`}
      className={({ isActive }) =>
        `group flex items-center gap-2.5 rounded-lg py-2 pl-3 pr-2.5 text-sm font-medium transition-all ${
          isActive ? "bg-loop text-white shadow-sm" : "text-ink/60 hover:bg-sky/50 hover:text-navy"
        }`
      }
    >
      {({ isActive }) => (
        <>
          <span className={`h-1.5 w-1.5 shrink-0 rounded-full ${isActive ? "bg-white" : "bg-ink/30 group-hover:bg-loop"}`} />
          <span className="flex-1 font-sora">{item.label}</span>
          {item.badge && badgeCount > 0 && (
            <span className={`min-w-[20px] rounded-full px-1.5 py-0.5 text-center text-[11px] font-bold ${isActive ? "bg-white text-loop" : "bg-red-500 text-white"}`}>{badgeCount}</span>
          )}
        </>
      )}
    </NavLink>
  );
}

function NavGroup({ item, visibleChildren, isActive, isOpen, onToggle, badges, onNavigate }) {
  return (
    <div>
      <button
        type="button"
        onClick={onToggle}
        data-testid={`nav-group-${slug(item.label)}`}
        aria-expanded={isOpen}
        className={`group flex w-full items-center gap-3 rounded-xl px-3.5 py-2.5 text-sm font-medium transition-all ${
          isActive ? "bg-sky/60 text-navy" : "text-ink/70 hover:bg-sky/50 hover:text-navy"
        }`}
      >
        <item.icon className="h-[18px] w-[18px]" strokeWidth={isActive ? 2.2 : 1.8} />
        <span className="flex-1 text-left font-sora">{item.label}</span>
        <ChevronDown className={`h-4 w-4 text-ink/50 transition-transform ${isOpen ? "rotate-180" : ""}`} />
      </button>
      {isOpen && (
        <div className="mt-1 space-y-0.5 border-l border-sky/70 pl-2.5 ml-5" data-testid={`nav-group-${slug(item.label)}-children`}>
          {visibleChildren.map((c) => (
            <ChildNavItem key={c.to} item={c} badgeCount={badges[c.to] || 0} onClick={onNavigate} />
          ))}
        </div>
      )}
    </div>
  );
}

/* ---- Collapsed (icon-only) rail variants ---- */
function CollapsedLeaf({ item, hasBadge }) {
  return (
    <NavLink
      to={item.to}
      title={item.label}
      aria-label={item.label}
      data-testid={`nav-collapsed-${slug(item.label)}`}
      className={({ isActive }) =>
        `group relative flex items-center justify-center rounded-xl py-2.5 transition-all ${
          isActive ? "bg-loop text-white shadow-sm loop-glow" : "text-ink/70 hover:bg-sky/50 hover:text-navy"
        }`
      }
    >
      {({ isActive }) => (
        <>
          <item.icon className="h-[20px] w-[20px]" strokeWidth={isActive ? 2.2 : 1.8} />
          {hasBadge && <span className="absolute right-2 top-1.5 h-2 w-2 rounded-full bg-red-500 ring-2 ring-white" />}
        </>
      )}
    </NavLink>
  );
}

function CollapsedGroup({ item, isActive, hasBadge, firstChild }) {
  return (
    <Link
      to={firstChild?.to || "#"}
      title={item.label}
      aria-label={item.label}
      data-testid={`nav-collapsed-${slug(item.label)}`}
      className={`group relative flex items-center justify-center rounded-xl py-2.5 transition-all ${
        isActive ? "bg-loop text-white shadow-sm loop-glow" : "text-ink/70 hover:bg-sky/50 hover:text-navy"
      }`}
    >
      <item.icon className="h-[20px] w-[20px]" strokeWidth={isActive ? 2.2 : 1.8} />
      {hasBadge && <span className="absolute right-2 top-1.5 h-2 w-2 rounded-full bg-red-500 ring-2 ring-white" />}
    </Link>
  );
}

export default function Layout({ children, onQuickAdd, badges = {}, notifCount = 0 }) {
  const { user, logout, has } = useAuth();
  const { theme, setTheme } = useTheme();
  const { pathname } = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);

  // Audit: record each screen the user opens (route change).
  useEffect(() => { logPageView(pathname); }, [pathname]);
  const [profileOpen, setProfileOpen] = useState(false);
  const profileRef = useRef(null);

  // Close the profile dropdown on any outside click or Escape (robust to z-index).
  useEffect(() => {
    if (!profileOpen) return;
    const onDown = (e) => { if (profileRef.current && !profileRef.current.contains(e.target)) setProfileOpen(false); };
    const onKey = (e) => { if (e.key === "Escape") setProfileOpen(false); };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => { document.removeEventListener("mousedown", onDown); document.removeEventListener("keydown", onKey); };
  }, [profileOpen]);
  const [openGroups, setOpenGroups] = useState({});
  const [collapsed, setCollapsed] = useState(() => {
    try { return localStorage.getItem("acquireLoop.sidebar.collapsed") === "true"; } catch { return false; }
  });
  const [hovered, setHovered] = useState(false);
  // Effective (visual) collapse: icon rail only when stored-collapsed AND not hovering.
  const visualCollapsed = collapsed && !hovered;

  const toggleCollapsed = () => setCollapsed((v) => {
    const nv = !v;
    try { localStorage.setItem("acquireLoop.sidebar.collapsed", String(nv)); } catch { /* ignore */ }
    setHovered(false);
    return nv;
  });

  const childVisible = (c) => !c.perm || has(c.perm);
  const groupHasActiveChild = (item) => (item.children || []).some((c) => pathname.startsWith(c.to));

  // Auto-expand the group that owns the active route (refresh / deep-link safe),
  // while preserving any group the user has manually opened.
  useEffect(() => {
    setOpenGroups((prev) => {
      const next = { ...prev };
      NAV.forEach((n) => { if (n.children && groupHasActiveChild(n)) next[n.label] = true; });
      return next;
    });
  }, [pathname]); // eslint-disable-line react-hooks/exhaustive-deps

  // Build the visible nav respecting permissions: hide gated leaves, hide gated
  // children, and drop any group that ends up with zero visible children.
  const visibleNav = NAV.map((item) => {
    if (item.children) {
      const kids = item.children.filter(childVisible);
      return kids.length ? { ...item, _kids: kids } : null;
    }
    return !item.perm || has(item.perm) ? item : null;
  }).filter(Boolean);

  const closeMobile = () => setMobileOpen(false);
  const toggleGroup = (label) => setOpenGroups((o) => ({ ...o, [label]: !o[label] }));

  const groupHasBadge = (item) => (item._kids || []).some((c) => c.badge && (badges[c.to] || 0) > 0);

  // `iconOnly` = render the slim icon rail. `showToggle` = include the desktop
  // collapse/expand control (hidden in the mobile drawer).
  const renderSidebar = (iconOnly, showToggle) => {
    const ToggleBtn = showToggle ? (
      <button
        type="button"
        onClick={toggleCollapsed}
        aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
        title={collapsed ? "Expand sidebar" : "Collapse sidebar"}
        aria-pressed={collapsed}
        data-testid={collapsed ? "sidebar-expand-btn" : "sidebar-collapse-btn"}
        className={`hidden items-center justify-center rounded-lg p-1.5 transition-colors lg:flex ${
          collapsed
            ? "bg-loop text-white shadow-sm loop-glow hover:bg-loophover"
            : "text-ink/50 hover:bg-sky/50 hover:text-navy"
        }`}
      >
        {collapsed ? <PanelLeftOpen className="h-5 w-5" /> : <PanelLeftClose className="h-[18px] w-[18px]" />}
      </button>
    ) : null;

    return (
      <div className="flex h-full flex-col">
        <div className={`flex h-16 items-center ${iconOnly ? "justify-center px-2" : "justify-between px-5"}`}>
          {iconOnly ? <LogoIcon className="h-8 w-8" /> : <LogoWordmark className="h-7" />}
          {!iconOnly && ToggleBtn}
        </div>
        <nav className={`flex-1 space-y-1 overflow-y-auto py-3 no-scrollbar ${iconOnly ? "px-2" : "px-3"}`}>
          {iconOnly && showToggle && <div className="mb-1 flex justify-center">{ToggleBtn}</div>}
          {visibleNav.map((item) =>
            item.children ? (
              iconOnly ? (
                <CollapsedGroup
                  key={item.label}
                  item={item}
                  isActive={groupHasActiveChild(item)}
                  hasBadge={groupHasBadge(item)}
                  firstChild={item._kids[0]}
                />
              ) : (
                <NavGroup
                  key={item.label}
                  item={item}
                  visibleChildren={item._kids}
                  isActive={groupHasActiveChild(item)}
                  isOpen={!!openGroups[item.label] || groupHasActiveChild(item)}
                  onToggle={() => toggleGroup(item.label)}
                  badges={badges}
                  onNavigate={closeMobile}
                />
              )
            ) : iconOnly ? (
              <CollapsedLeaf key={item.to} item={item} hasBadge={item.badge && (badges[item.to] || 0) > 0} />
            ) : (
              <LeafNavItem key={item.to} item={item} badgeCount={badges[item.to] || 0} onClick={closeMobile} />
            )
          )}
        </nav>
        <div className="border-t border-sky/60 p-3">
          {iconOnly ? (
            <p className="text-center font-sora text-[10px] font-semibold text-ink/40">v1.6</p>
          ) : (
            <div className="rounded-xl bg-mist px-3 py-2.5">
              <p className="font-sora text-xs font-semibold text-navy">AcquireLoop</p>
              <p className="text-[11px] text-ink/50">by Proffus · v1.6</p>
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="flex min-h-screen bg-mist">
      {/* Desktop sidebar (manual collapse + hover-to-peek) */}
      <aside
        data-testid="desktop-sidebar"
        data-collapsed={visualCollapsed}
        onMouseEnter={() => collapsed && setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        className={`fixed inset-y-0 left-0 z-30 hidden border-r border-sky/60 bg-white transition-[width] duration-200 lg:block ${
          visualCollapsed ? "lg:w-[72px]" : "lg:w-64"
        } ${collapsed && hovered ? "shadow-2xl" : ""}`}
      >
        {renderSidebar(visualCollapsed, true)}
      </aside>

      {/* Mobile drawer (always full, never icon-only) */}
      {mobileOpen && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div className="absolute inset-0 bg-navy/30 backdrop-blur-sm" onClick={() => setMobileOpen(false)} />
          <div className="absolute inset-y-0 left-0 w-72 bg-white shadow-xl">
            <button onClick={() => setMobileOpen(false)} className="absolute right-3 top-4 text-ink/50" data-testid="close-mobile-nav">
              <X className="h-5 w-5" />
            </button>
            {renderSidebar(false, false)}
          </div>
        </div>
      )}

      <div className={`flex min-w-0 flex-1 flex-col transition-[padding] duration-200 ${collapsed ? "lg:pl-[72px]" : "lg:pl-64"}`}>
        {/* Top bar */}
        <header className="sticky top-0 z-20 flex h-16 items-center gap-2 border-b border-sky/60 bg-white/80 px-4 backdrop-blur-md sm:px-6">
          <button className="lg:hidden text-ink" onClick={() => setMobileOpen(true)} data-testid="open-mobile-nav">
            <Menu className="h-6 w-6" />
          </button>
          <LogoIcon className="h-8 w-8 lg:hidden" />

          <GlobalSearch />

          <div className="ml-auto flex items-center gap-2">
            {has("leads.create_manual") && (
              <button
                onClick={onQuickAdd}
                data-testid="quick-add-lead-button"
                className="flex items-center gap-2 rounded-xl bg-loop px-3 py-2 text-sm font-semibold text-white transition-colors hover:bg-loophover"
              >
                <Plus className="h-4 w-4" />
                <span className="hidden md:inline">Quick Add</span>
              </button>
            )}
            <NotificationBell />

            <div className="relative" ref={profileRef}>
              <button
                onClick={() => setProfileOpen((v) => !v)}
                data-testid="profile-button"
                className="flex items-center gap-2 rounded-xl border border-sky py-1 pl-1 pr-2 transition-colors hover:bg-mist"
              >
                <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-navy text-xs font-bold text-white">
                  {initials(user?.name)}
                </span>
                <ChevronDown className="hidden h-4 w-4 text-ink/50 sm:block" />
              </button>
              {profileOpen && (
                <div className="absolute right-0 z-30 mt-2 w-56 rounded-xl border border-sky bg-white p-2 shadow-xl">
                    <div className="px-3 py-2">
                      <p className="font-sora text-sm font-semibold text-navy">{user?.name}</p>
                      <p className="text-xs text-ink/50">{user?.email}</p>
                      <span className="mt-1 inline-block rounded-md bg-sky/60 px-2 py-0.5 text-[11px] font-medium text-navy">{user?.role}</span>
                    </div>

                    {/* Appearance: Light / Dark theme toggle */}
                    <div className="mt-1 border-t border-sky/60 px-3 pt-2.5 pb-1">
                      <p className="mb-1.5 text-[11px] font-semibold uppercase tracking-wide text-ink/45">Appearance</p>
                      <div className="flex rounded-lg border border-sky bg-mist p-0.5" data-testid="theme-toggle">
                        <button
                          onClick={() => setTheme("light")}
                          data-testid="theme-light"
                          aria-pressed={theme === "light"}
                          className={`flex flex-1 items-center justify-center gap-1.5 rounded-md px-2 py-1.5 text-xs font-semibold transition-colors ${theme === "light" ? "bg-white text-navy shadow-sm" : "text-ink/55 hover:text-navy"}`}
                        >
                          <Sun className="h-3.5 w-3.5" /> Light
                        </button>
                        <button
                          onClick={() => setTheme("dark")}
                          data-testid="theme-dark"
                          aria-pressed={theme === "dark"}
                          className={`flex flex-1 items-center justify-center gap-1.5 rounded-md px-2 py-1.5 text-xs font-semibold transition-colors ${theme === "dark" ? "bg-white text-navy shadow-sm" : "text-ink/55 hover:text-navy"}`}
                        >
                          <Moon className="h-3.5 w-3.5" /> Dark
                        </button>
                      </div>
                    </div>

                    <button
                      onClick={logout}
                      data-testid="logout-button"
                      className="mt-1 flex w-full items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium text-red-600 transition-colors hover:bg-red-50"
                    >
                      <LogOut className="h-4 w-4" /> Log out
                    </button>
                </div>
              )}
            </div>
          </div>
        </header>

        <main className="flex-1 px-4 py-6 sm:px-6 lg:px-8">{children}</main>
      </div>
    </div>
  );
}
